import axios from "axios";
import type { AxiosRequestConfig } from "axios";
import type { ZodSchema, output } from "zod";
import { ResponseError } from "./responseError";
import type { HTTPMethod } from "~/utils/types";
import { updateLoggedIn, deleteLoggedIn } from "~/utils/logInCookiesCRUD";
import { deleteLoggedAsAdmin } from "~/utils/adminCookiesCRUD";

import { fromZodError } from "zod-validation-error";
// import * as Sentry from '@sentry/vue';
// @ts-ignore
import { useRuntimeConfig } from "#app";

axios.defaults.withCredentials = true;
axios.defaults.withXSRFToken = true;

type FetcherConfig<Schema extends ZodSchema<any, any> | null> = {
  readonly method: HTTPMethod;
  readonly schema?: Schema;
  readonly body?: object;
  readonly params?: object;
  readonly config?: AxiosRequestConfig;
  readonly customApiUrl?: string;
  readonly meta?: boolean;
};

export async function getCsrfToken() {
  try {
    const { API_URL } = useRuntimeConfig().public;

    if (!API_URL) {
      throw new ResponseError("API_URL environment variable is not set", 111);
    }

    const response = await axios.get(`${API_URL}/csrf-cookie`);
    return response.data;
  } catch (err: any) {
    console.error("Error in fetcher on CSRF:", err);
    throw err;
  }
}

export async function fetcher<Schema extends ZodSchema<any, any> | null>(
  path: string,
  {
    method,
    body,
    config,
    schema,
    customApiUrl,
    params,
    meta,
  }: FetcherConfig<Schema>,
  retryAttempted = false, // new flag to avoid infinite retry loops
): Promise<Schema extends ZodSchema ? output<Schema> : null> {
  try {
    const { API_URL } = useRuntimeConfig().public;

    if (!API_URL) {
      throw new ResponseError("API_URL environment variable is not set", 111);
    }

    const isFormData = body instanceof FormData;
    const response = await axios.request({
      url: `${customApiUrl || API_URL}${path}`,
      method,
      headers: {
        ...(isFormData ? {} : { "Content-Type": "application/json" }),
        "Allow-Access-Control-Origin": "*",
        Accept: "application/json",
        ...config?.headers,
      },
      data: body ? (isFormData ? body : JSON.stringify(body)) : undefined,
      params,
      ...config,
    });

    updateLoggedIn();

    let data;
    if (meta) {
      return response.data;
    } else {
      data = response.data.data;
    }

    // Don't validate CSRF route payload
    if (path === "/csrf-cookie") {
      return {} as any;
    }

    if (schema) {
      const parsedData = schema.safeParse(data);
      if (!parsedData.success) {
        console.log("Invalid schema:", data);
        console.log("Invalid schema error:", parsedData.error);
        throw new ResponseError(fromZodError(parsedData.error).toString(), 409);
      }
      return parsedData.data;
    }

    return data;
  } catch (err: any) {
    // const router = useRouter();
    // const route = useRoute();
    if (err?.response?.status === 419) {
      if (!retryAttempted) {
        try {
          await getCsrfToken();
          return await fetcher(
            path,
            { method, body, config, schema, customApiUrl },
            true,
          );
        } catch (retryErr: any) {
          // Sentry.captureException(retryErr);
          showError({
            message:
              "Nie udało się odświeżyć ciasteczka CSRF. Spróbuj ponownie później.",
          });
          throw retryErr;
        }
      } else {
        // Sentry.captureException(err);
        showError({
          message:
            "Nie udało się odświeżyć ciasteczka CSRF. Spróbuj ponownie później.",
        });
        throw err;
      }
    }

    if (err?.response?.status === 401) {
      deleteLoggedIn();
      deleteLoggedAsAdmin();
      throw new ResponseError(err, 401);
    } else if (err?.response?.status === 403) {
      deleteLoggedIn();
      deleteLoggedAsAdmin();
      throw new ResponseError(err, 403);
    } else if (err?.response?.status === 404) {
      // Sentry.captureException(err);
      return;
    } else if (err?.response?.status === 422 && err?.response?.data?.errors) {
      const errors: Record<string, string[]> = err.response.data.errors;
      const firstError = errors[Object.keys(errors)[0]][0];
      // Sentry.captureException(err);
      throw new ResponseError(firstError, 422);
    }

    if (
      typeof err === "object" &&
      err !== null &&
      "message" in err &&
      err.message === "Failed to fetch"
    ) {
      showError({
        message:
          "Wystąpił błąd podczas pobierania danych. Zostałeś wylogowany. Spróbuj ponownie później.",
      });
      throw err;
    }

    // Sentry.captureException(err);
    // showError({
    //   message:
    //     "Coś poszło nie tak przy pobieraniu danych. Spróbuj ponownie później.",
    // });
    throw err;
  }
}
